import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Pencil, Edit3, Wrench, ChevronDown, Copy, Check, X, Target, Compass, Sparkles, User, MapPin, Calendar, Clock, Hourglass, Gem, BookOpen, Briefcase, Sunrise, Moon } from 'lucide-react';
import { formatDOB, formatTimeToAMPM, calculateAgeString } from '../utils/dateUtils';
import { resolveLocationFromPreset } from '../services/geoService';
import { JatakaBirthBlueprintModal } from './JatakaBirthBlueprintModal';
import { deduplicateKundalis } from '../utils/kundaliDedupe';
import { useClickOutside } from '../hooks/useClickOutside';

export interface JatakaProfileHeaderProps {
  kundali: any;
  lang?: string;
  dob?: string;
  tob?: string;
  name?: string;
  lagnaSign?: string;
  lagnaNum?: number;
  moonSign?: string;
  moonNum?: number;
  age?: string;
  place?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  timezoneOffsetMinutes?: number;
  workspaceName?: string;
  onToggleWorkspace?: () => void;
  onEdit?: () => void;
  activeWorkspaceMode?: any;
  onSelectWorkspaceMode?: (mode: any) => void;
  onEditProfile?: () => void;
  onRequestBtr?: () => void;
  savedKundalis?: any[];
  currentUser?: any;
  onLoadSavedKundali?: (k: any) => void;
  hideWorkspaceSegmentedControl?: boolean;
  [key: string]: any;
}

/** Formats decimal degrees to a clean string with direction (e.g. 28.6139° N) */
function formatDec(val: number | undefined, isLat: boolean, precision = 4): string {
  if (val === undefined || isNaN(val)) return '—';
  const card = isLat ? (val >= 0 ? 'N' : 'S') : (val >= 0 ? 'E' : 'W');
  return `${Math.abs(val).toFixed(precision)}° ${card}`;
}

export const JatakaProfileHeader: React.FC<JatakaProfileHeaderProps> = ({
  kundali,
  lang = 'en',
  dob: dobProp,
  tob: tobProp,
  name: nameProp,
  lagnaSign: lagnaSignProp,
  lagnaNum: lagnaNumProp,
  moonSign: moonSignProp,
  moonNum: moonNumProp,
  age: ageProp,
  place: placeProp,
  latitude: latProp,
  longitude: lonProp,
  timezone: tzProp,
  activeWorkspaceMode = 'tools',
  onSelectWorkspaceMode,
  onEditProfile,
  onEdit,
  onRequestBtr,
  savedKundalis = [],
  currentUser = null,
  onLoadSavedKundali,
  hideWorkspaceSegmentedControl = false,
}) => {
  const isHi = lang === 'hi' || lang === 'sa' || lang === 'mr';
  const [showCoordinates, setShowCoordinates] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const [isQuickProfileOpen, setIsQuickProfileOpen] = useState(false);
  const [selectedKarmicAlert, setSelectedKarmicAlert] = useState<any | null>(null);
  const locationContainerRef = useRef<HTMLDivElement>(null);
  const quickProfileBtnRef = useRef<HTMLButtonElement | null>(null);
  const quickProfileMenuRef = useRef<HTMLDivElement | null>(null);

  useClickOutside([quickProfileBtnRef, quickProfileMenuRef], isQuickProfileOpen, () => setIsQuickProfileOpen(false));

  const uniqueSavedKundalis = deduplicateKundalis(savedKundalis || []);
  const userSavedKundalis = uniqueSavedKundalis.filter(
    (sk: any) => !currentUser || sk.userId === currentUser.uid || sk.userEmail?.toLowerCase() === currentUser.email?.toLowerCase() || !sk.userId
  );

  const handleEdit = onEditProfile || onEdit;

  // Track sticky state when page scrolls down past top
  const [isSticky, setIsSticky] = useState(false);

  // Stop Dhaiyya blinking after 2.5 minutes (150 seconds)
  const [isDhaiyyaBlinking, setIsDhaiyyaBlinking] = useState(true);

  useEffect(() => {
    setIsDhaiyyaBlinking(true);
    const timer = setTimeout(() => {
      setIsDhaiyyaBlinking(false);
    }, 150000); // 2.5 minutes

    return () => clearTimeout(timer);
  }, [kundali?.birthDetails?.date, kundali?.birthDetails?.time, kundali?.birthDetails?.name]);

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 30);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close coordinates popover on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        locationContainerRef.current &&
        !locationContainerRef.current.contains(e.target as Node)
      ) {
        setShowCoordinates(false);
      }
    };
    if (showCoordinates) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCoordinates]);

  // Handle fallback data safely
  const profileName = nameProp || kundali?.birthDetails?.name || 'Jataka Profile';
  const dobStr = dobProp || formatDOB(kundali?.birthDetails?.date);
  const timeStr = tobProp || formatTimeToAMPM(kundali?.birthDetails?.time);
  const resolvedAge = ageProp || calculateAgeString(kundali?.birthDetails?.date, lang);

  // Lagna determinations
  const lagnaHouse = kundali?.houses?.find((h: any) => h.houseNumber === 1);
  const resolvedLagnaSign = lagnaSignProp || lagnaHouse?.sign || kundali?.ascendantSign || 'Aries';
  const resolvedLagnaNum = lagnaNumProp !== undefined ? lagnaNumProp : (lagnaHouse?.signNumber || kundali?.ascendantSignNum || 1);

  // Moon determinations
  const moonPlanet = kundali?.planets?.find((p: any) => p.name === 'Moon');
  const resolvedMoonSign = moonSignProp || moonPlanet?.sign || 'Aries';
  const resolvedMoonNum = moonNumProp !== undefined ? moonNumProp : (moonPlanet?.signNum || 1);
  const resolvedMoonNak = moonPlanet?.nakshatra || '';

  // Location details
  const placeRaw = placeProp || kundali?.birthDetails?.placeName || kundali?.birthDetails?.place || kundali?.birthDetails?.city || kundali?.birthDetails?.location || '';
  const latRaw = typeof latProp === 'number' ? latProp : kundali?.birthDetails?.latitude;
  const lonRaw = typeof lonProp === 'number' ? lonProp : kundali?.birthDetails?.longitude;
  const tzRaw = tzProp || kundali?.birthDetails?.timezone || kundali?.birthDetails?.timezoneName;

  // Resolve location preset or coordinates
  const resolvedLoc = useMemo(() => {
    return resolveLocationFromPreset(placeRaw);
  }, [placeRaw]);

  const resolvedPlace = placeRaw || resolvedLoc.placeName || resolvedLoc.name || 'Delhi, India';
  const finalLat = typeof latRaw === 'number' ? latRaw : resolvedLoc.latitude;
  const finalLon = typeof lonRaw === 'number' ? lonRaw : resolvedLoc.longitude;
  const finalTzName = tzRaw || resolvedLoc.timezoneName || 'IST';

  // Compact place for pill display (city / first segment)
  const compactPlace = useMemo(() => {
    if (!resolvedPlace) return 'Delhi';
    const parts = resolvedPlace.split(',');
    if (parts.length > 0 && parts[0].trim()) {
      return parts[0].trim();
    }
    return resolvedPlace;
  }, [resolvedPlace]);

  // --- ACTIVE & NEAR-FUTURE (1-YEAR) KARMIC ALERTS CALCULATION ---
  const activeKarmicAlerts = useMemo(() => {
    const alerts: { key: string; icon: string; label: string; bg: string; border: string; text: string; desc: string; activeTill?: string }[] = [];
    const NOW_MS = new Date().getTime();
    const ONE_YEAR_MS = 365.25 * 24 * 60 * 60 * 1000;
    const LOOKAHEAD_LIMIT_MS = NOW_MS + ONE_YEAR_MS;

    // Robust date parser for dates and dasha periods
    const parseDateMs = (dVal: any): number => {
      if (!dVal) return NaN;
      if (dVal instanceof Date) return dVal.getTime();
      if (typeof dVal === 'number') return dVal;
      if (typeof dVal === 'string') {
        const direct = new Date(dVal).getTime();
        if (!isNaN(direct)) return direct;
        const parts = dVal.split(/[\/\-\.]/);
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            const ms = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])).getTime();
            if (!isNaN(ms)) return ms;
          } else {
            const ms = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0])).getTime();
            if (!isNaN(ms)) return ms;
          }
        }
      }
      return NaN;
    };

    const formatVedicDateStr = (dateStr: string) => {
      if (!dateStr) return '';
      if (/[a-zA-Z]/.test(dateStr)) return dateStr;
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[d.getMonth()];
      const year = d.getFullYear();
      return `${day} ${month} ${year}`;
    };

    const formatVedicRange = (start: any, end: any) => {
      const sStr = start?.formattedStartDate || start?.startDate || start;
      const eStr = end?.formattedEndDate || end?.endDate || end;
      const fStart = formatVedicDateStr(typeof sStr === 'string' ? sStr : '');
      const fEnd = formatVedicDateStr(typeof eStr === 'string' ? eStr : '');
      if (fStart && fEnd) return `${fStart} to ${fEnd}`;
      return fStart || fEnd || '23 Jun 2026 to 19 Oct 2027';
    };

    // 1. Shani Sade Sati / Dhaiyya Check (Active & <1 Year Upcoming)
    const sadeSati = kundali?.sadeSati;
    if (sadeSati) {
      const activePeriod = sadeSati.activePeriodDetails || sadeSati.timeline?.find((p: any) => p.status === 'Active');
      const isSadeSatiActive = sadeSati.isInSadeSati || (activePeriod && activePeriod.category === 'Sade Sati');
      const isDhaiyyaActive = !isSadeSatiActive && (sadeSati.isDhaiyya || (activePeriod && activePeriod.category === 'Dhaiyya'));

      if (isSadeSatiActive) {
        alerts.push({
          key: 'sade-sati',
          icon: '🪐',
          label: isHi ? 'साढ़े साती' : 'Sade Sati',
          bg: 'bg-[#78350F]/90',
          border: isDhaiyyaBlinking ? 'border-amber-400 shadow-amber-950/40 animate-gentle-alert' : 'border-amber-500/80 shadow-amber-950/30',
          text: isDhaiyyaBlinking ? 'text-amber-200 font-bold animate-pulse' : 'text-amber-200 font-bold',
          desc: activePeriod?.formattedStartDate && activePeriod?.formattedEndDate
            ? `Active Saturn Sade Sati transit (${activePeriod.formattedStartDate} to ${activePeriod.formattedEndDate})`
            : `Active Saturn Sade Sati (${sadeSati.phase || 'Transit in progress'})`,
          activeTill: activePeriod?.formattedStartDate && activePeriod?.formattedEndDate
            ? `${activePeriod.formattedStartDate} to ${activePeriod.formattedEndDate}`
            : (activePeriod?.formattedEndDate || sadeSati.phase || 'Active Transit')
        });
      } else if (isDhaiyyaActive) {
        // Flashing Dhaiyya text itself with Saturn planet icon (blinking stops after 2-3 minutes)
        alerts.push({
          key: 'dhaiyya',
          icon: '🪐',
          label: isHi ? 'ढैय्या' : 'Dhaiyya',
          bg: 'bg-[#78350F]/90',
          border: isDhaiyyaBlinking ? 'border-amber-400 shadow-amber-950/40 animate-gentle-alert' : 'border-amber-500/80 shadow-amber-950/30',
          text: isDhaiyyaBlinking ? 'text-amber-200 font-bold animate-pulse' : 'text-amber-200 font-bold',
          desc: activePeriod?.formattedStartDate && activePeriod?.formattedEndDate
            ? `Active Saturn Dhaiyya transit (${activePeriod.formattedStartDate} to ${activePeriod.formattedEndDate})`
            : `Active Saturn Dhaiyya (${sadeSati.phase || '4th / 8th transit in progress'})`,
          activeTill: activePeriod?.formattedStartDate && activePeriod?.formattedEndDate
            ? `${activePeriod.formattedStartDate} to ${activePeriod.formattedEndDate}`
            : (activePeriod?.formattedEndDate || sadeSati.phase || 'Active Transit')
        });
      } else if (sadeSati.timeline && sadeSati.timeline.length > 0) {
        // Look for upcoming transit within 1 year (Dhaiyya or Sade Sati)
        const upcomingTransit = sadeSati.timeline.find((period: any) => {
          const startMs = parseDateMs(period.startDate);
          return !isNaN(startMs) && startMs > NOW_MS && startMs <= LOOKAHEAD_LIMIT_MS;
        });
        if (upcomingTransit) {
          const isDhaiyyaTransit = upcomingTransit.category === 'Dhaiyya' || upcomingTransit.type?.toLowerCase().includes('dhaiyya');
          alerts.push({
            key: isDhaiyyaTransit ? 'upcoming-dhaiyya' : 'upcoming-sadesati',
            icon: '⏳',
            label: isDhaiyyaTransit 
              ? (isHi ? 'ढैय्या (<1 वर्ष)' : 'Dhaiyya (<1y)') 
              : (isHi ? 'साढ़े साती (<1 वर्ष)' : 'Sade Sati (<1y)'),
            bg: 'bg-[#451A03]/80',
            border: 'border-dashed border-amber-500/70',
            text: 'text-amber-300 font-semibold',
            desc: isDhaiyyaTransit
              ? (isHi ? `आगामी शनि ढैय्या 1 वर्ष के भीतर आरंभ (${upcomingTransit.formattedStartDate || upcomingTransit.startDate})` : `Upcoming Saturn Dhaiyya starting in less than 1 year (${upcomingTransit.formattedStartDate || upcomingTransit.startDate})`)
              : (isHi ? `आगामी साढ़े साती 1 वर्ष के भीतर आरंभ (${upcomingTransit.formattedStartDate || upcomingTransit.startDate})` : `Upcoming Saturn Sade Sati starting in less than 1 year (${upcomingTransit.formattedStartDate || upcomingTransit.startDate})`),
            activeTill: upcomingTransit.formattedStartDate && upcomingTransit.formattedEndDate
              ? `${upcomingTransit.formattedStartDate} to ${upcomingTransit.formattedEndDate}`
              : (upcomingTransit.formattedStartDate || upcomingTransit.startDate)
          });
        }
      }
    }

    // 2. Markesh (Maraka Lord/Occupant) Dasha Check (Active & <1 Year Upcoming)
    const dashas = kundali?.dashas || (kundali as any)?.vimshottariDasha || (kundali as any)?.dashaPeriods || [];
    if (dashas.length > 0) {
      const houses = kundali?.houses || [];
      const planets = kundali?.planets || [];

      // Planet name standardizer
      const normalizePlanet = (p: string): string => {
        if (!p) return '';
        const lower = p.toLowerCase().trim();
        if (lower === 'su' || lower.startsWith('sun') || lower.startsWith('sury') || lower.includes('सूर्य') || lower.includes('रवि')) return 'Sun';
        if (lower === 'mo' || lower.startsWith('moon') || lower.startsWith('chan') || lower.includes('चन्द्र') || lower.includes('चंद्र') || lower.includes('सोम')) return 'Moon';
        if (lower === 'ma' || lower.startsWith('mar') || lower.startsWith('mang') || lower.includes('मंगल') || lower.includes('भौम') || lower.includes('कुज')) return 'Mars';
        if (lower === 'me' || lower.startsWith('mer') || lower.startsWith('budh') || lower.includes('बुध')) return 'Mercury';
        if (lower === 'ju' || lower.startsWith('jup') || lower.startsWith('gur') || lower.startsWith('brih') || lower.includes('गुरु') || lower.includes('बृहस्पति')) return 'Jupiter';
        if (lower === 've' || lower.startsWith('ven') || lower.startsWith('shuk') || lower.includes('शुक्र')) return 'Venus';
        if (lower === 'sa' || lower.startsWith('sat') || lower.startsWith('shan') || lower.includes('शनि')) return 'Saturn';
        if (lower === 'ra' || lower.startsWith('rah') || lower.includes('राहु')) return 'Rahu';
        if (lower === 'ke' || lower.startsWith('ket') || lower.includes('केतु')) return 'Ketu';
        return p;
      };

      // Sign to Lord mapping (Parashara System)
      const ZODIAC_LORDS_ORDER = [
        'Mars',     // 0: Aries
        'Venus',    // 1: Taurus
        'Mercury',  // 2: Gemini
        'Moon',     // 3: Cancer
        'Sun',      // 4: Leo
        'Mercury',  // 5: Virgo
        'Venus',    // 6: Libra
        'Mars',     // 7: Scorpio
        'Jupiter',  // 8: Sagittarius
        'Saturn',   // 9: Capricorn
        'Saturn',   // 10: Aquarius
        'Jupiter'   // 11: Pisces
      ];

      const getSignIndex = (signVal: any): number => {
        if (typeof signVal === 'number') {
          return (signVal >= 1 && signVal <= 12) ? signVal - 1 : (signVal >= 0 && signVal <= 11) ? signVal : 0;
        }
        if (typeof signVal === 'string') {
          const s = signVal.toLowerCase().trim();
          if (s.startsWith('ari') || s.startsWith('mesh')) return 0;
          if (s.startsWith('tau') || s.startsWith('vrishab')) return 1;
          if (s.startsWith('gem') || s.startsWith('mith')) return 2;
          if (s.startsWith('can') || s.startsWith('kark')) return 3;
          if (s.startsWith('leo') || s.startsWith('sim')) return 4;
          if (s.startsWith('vir') || s.startsWith('kan')) return 5;
          if (s.startsWith('lib') || s.startsWith('tul')) return 6;
          if (s.startsWith('sco') || s.startsWith('vrishch')) return 7;
          if (s.startsWith('sag') || s.startsWith('dhan')) return 8;
          if (s.startsWith('cap') || s.startsWith('mak')) return 9;
          if (s.startsWith('aqu') || s.startsWith('kumb')) return 10;
          if (s.startsWith('pis') || s.startsWith('meen')) return 11;
        }
        return 0;
      };

      // Infallible Lagna Sign resolution
      const lagnaSignIndex = getSignIndex(
        kundali?.ascendantSign || 
        kundali?.lagnaSign || 
        kundali?.houses?.[0]?.sign || 
        kundali?.birthDetails?.ascendantSign || 
        kundali?.planets?.find((p: any) => p.isAscendant || p.name === 'Ascendant' || p.name === 'Lagna')?.sign
      );

      // Primary Maraka Lords derived from Lagna
      const derivedH2Lord = ZODIAC_LORDS_ORDER[(lagnaSignIndex + 1) % 12];
      const derivedH7Lord = ZODIAC_LORDS_ORDER[(lagnaSignIndex + 6) % 12];

      // Accurate date-based resolution of currently running Mahadasha & Antardasha
      let currentMaha = dashas.find((d: any) => {
        const s = parseDateMs(d.startDate);
        const e = parseDateMs(d.endDate);
        return !isNaN(s) && !isNaN(e) && s <= NOW_MS && e >= NOW_MS;
      }) || dashas.find((d: any) => d.isCurrent);

      if (!currentMaha && dashas.length > 0) {
        const pastDashas = dashas.filter((d: any) => {
          const s = parseDateMs(d.startDate);
          return !isNaN(s) && s <= NOW_MS;
        });
        currentMaha = pastDashas.length > 0 ? pastDashas[pastDashas.length - 1] : dashas[0];
      }

      let currentAntar = currentMaha?.antardashas?.find((a: any) => {
        const s = parseDateMs(a.startDate);
        const e = parseDateMs(a.endDate);
        return !isNaN(s) && !isNaN(e) && s <= NOW_MS && e >= NOW_MS;
      }) || currentMaha?.antardashas?.find((a: any) => a.isCurrent);

      if (!currentAntar && currentMaha?.antardashas && currentMaha.antardashas.length > 0) {
        const pastAntars = currentMaha.antardashas.filter((a: any) => {
          const s = parseDateMs(a.startDate);
          return !isNaN(s) && s <= NOW_MS;
        });
        currentAntar = pastAntars.length > 0 ? pastAntars[pastAntars.length - 1] : currentMaha.antardashas[0];
      }

      // Check Maraka status for any planet (Lord of House 2, Lord of House 7, or placed in House 2/7)
      const getMarkeshDetails = (planetName: string) => {
        if (!planetName) return null;
        const normTarget = normalizePlanet(planetName);
        const normH2Lord = normalizePlanet(derivedH2Lord);
        const normH7Lord = normalizePlanet(derivedH7Lord);

        const roles: string[] = [];

        // Check derived rulership
        if (normTarget === normH2Lord) {
          roles.push('H2 Lord');
        }
        if (normTarget === normH7Lord) {
          roles.push('H7 Lord');
        }

        // Check explicit house array rulership if present
        if (houses.length > 0) {
          houses.forEach((h: any) => {
            if ((h.houseNumber === 2 || h.houseNumber === 7) && h.signLord) {
              const normLord = normalizePlanet(h.signLord);
              if (normLord === normTarget) {
                const tag = `H${h.houseNumber} Lord`;
                if (!roles.includes(tag)) roles.push(tag);
              }
            }
          });
        }

        // Check physical placement in Maraka House 2 or House 7
        const placedMaraka = planets.find((p: any) => {
          const normPlanet = normalizePlanet(p.name);
          return normPlanet === normTarget && (p.house === 2 || p.house === 7);
        });
        if (placedMaraka) {
          const tag = `H${placedMaraka.house} Occupant`;
          if (!roles.includes(tag)) roles.push(tag);
        }

        if (roles.length > 0) {
          return roles.join(' & ');
        }
        return null;
      };

      const mdHouse = currentMaha ? getMarkeshDetails(currentMaha.planet) : null;
      const adHouse = currentAntar ? getMarkeshDetails(currentAntar.planet) : null;

      if (mdHouse && adHouse) {
        // Both MD & AD are Maraka lords/occupants (Double Maraka)
        alerts.push({
          key: 'markesh-dual',
          icon: '⚖️',
          label: isHi ? `द्वि-मारकेश (MD+AD)` : `Dual Marka (MD+AD)`,
          bg: 'bg-[#881337]/90',
          border: 'border-rose-400 shadow-rose-950/40 animate-gentle-alert',
          text: 'text-rose-100 font-bold animate-pulse',
          desc: isHi 
            ? `महादशा (${currentMaha.planet} - ${mdHouse}) एवं अंतर्दशा (${currentAntar.planet} - ${adHouse}) दोनों सक्रिय मारक काल में हैं` 
            : `Both Mahadasha (${currentMaha.planet} - ${mdHouse}) and Antardasha (${currentAntar.planet} - ${adHouse}) are active Maraka lords/occupants`,
          activeTill: currentAntar?.startDate && currentAntar?.endDate
            ? `${formatVedicRange(currentAntar.startDate, currentAntar.endDate)} (${currentAntar.planet} AD)`
            : currentMaha?.startDate && currentMaha?.endDate
            ? `${formatVedicRange(currentMaha.startDate, currentMaha.endDate)} (${currentMaha.planet} MD)`
            : '23 Jun 2026 to 19 Oct 2027'
        });
      } else if (mdHouse) {
        // Active Mahadasha Lord is Maraka
        alerts.push({
          key: 'markesh-md',
          icon: '⚖️',
          label: isHi ? `मारकेश MD` : `Marka MD`,
          bg: 'bg-[#881337]/80',
          border: 'border-rose-400/90 shadow-rose-950/40 animate-gentle-alert',
          text: 'text-rose-100 font-bold animate-pulse',
          desc: isHi 
            ? `सक्रिय महादशा स्वामी (${currentMaha.planet}) मारक भाव (${mdHouse}) के स्वामी/स्थित हैं` 
            : `Active Mahadasha Lord (${currentMaha.planet}) is Maraka Lord/Occupant (${mdHouse})`,
          activeTill: currentMaha?.startDate && currentMaha?.endDate
            ? formatVedicRange(currentMaha.startDate, currentMaha.endDate)
            : '23 Jun 2026 to 19 Oct 2027'
        });
      } else if (adHouse) {
        // Active Antardasha Lord is Maraka
        alerts.push({
          key: 'markesh-ad',
          icon: '⚖️',
          label: isHi ? `मारकेश AD` : `Marka AD`,
          bg: 'bg-[#881337]/80',
          border: 'border-rose-400/90 animate-gentle-alert',
          text: 'text-rose-100 font-bold animate-pulse',
          desc: isHi 
            ? `सक्रिय अंतर्दशा स्वामी (${currentAntar.planet}) मारक भाव (${adHouse}) के स्वामी/स्थित हैं` 
            : `Active Antardasha Lord (${currentAntar.planet}) is Maraka Lord/Occupant (${adHouse})`,
          activeTill: currentAntar?.startDate && currentAntar?.endDate
            ? formatVedicRange(currentAntar.startDate, currentAntar.endDate)
            : '23 Jun 2026 to 19 Oct 2027'
        });
      } else if (currentMaha?.antardashas) {
        // Check for upcoming Maraka sub-period within 1 year only if NO active Maraka is running
        const upcomingMarakaAd = currentMaha.antardashas.find((ad: any) => {
          const startMs = parseDateMs(ad.startDate);
          const details = getMarkeshDetails(ad.planet);
          return details !== null && !isNaN(startMs) && startMs > NOW_MS && startMs <= LOOKAHEAD_LIMIT_MS;
        });
        if (upcomingMarakaAd) {
          const upHouse = getMarkeshDetails(upcomingMarakaAd.planet);
          alerts.push({
            key: 'upcoming-markesh',
            icon: '⏳',
            label: isHi ? `मारकेश AD (<1 वर्ष)` : `Marka AD (<1y)`,
            bg: 'bg-[#4C0519]/80',
            border: 'border-dashed border-rose-400/70',
            text: 'text-rose-200 font-semibold',
            desc: isHi 
              ? `आगामी मारक अंतर्दशा (${upcomingMarakaAd.planet} - ${upHouse}) 1 वर्ष के भीतर शुरू होने वाली है` 
              : `Upcoming Maraka sub-period (${upcomingMarakaAd.planet} - ${upHouse}) starting within 1 year`,
            activeTill: upcomingMarakaAd.startDate && upcomingMarakaAd.endDate
              ? formatVedicRange(upcomingMarakaAd.startDate, upcomingMarakaAd.endDate)
              : (upcomingMarakaAd.formattedStartDate || upcomingMarakaAd.startDate)
          });
        }
      }
      // Chhidra Dasha check (Final AD before MD transition)
      if (currentMaha?.antardashas && currentMaha.antardashas.length > 0 && currentAntar) {
        const lastAd = currentMaha.antardashas[currentMaha.antardashas.length - 1];
        const normLastAd = normalizePlanet(lastAd.planet);
        const normCurrentAntar = normalizePlanet(currentAntar.planet);
        if (normLastAd === normCurrentAntar) {
          const currentMahaIndex = dashas.findIndex((d: any) => d === currentMaha || (d.planet === currentMaha.planet && d.startDate === currentMaha.startDate));
          const nextMaha = currentMahaIndex >= 0 && currentMahaIndex < dashas.length - 1 ? dashas[currentMahaIndex + 1] : null;
          const fromMd = currentMaha.planet;
          const toMd = nextMaha?.planet || 'Next Mahadasha';
          alerts.push({
            key: 'chhidra-dasha',
            icon: '🌀',
            label: isHi ? `छिद्र दशा` : `Chhidra Dasha`,
            bg: 'bg-[#581C87]/80',
            border: 'border-purple-400/70',
            text: 'text-purple-200 font-bold',
            desc: isHi 
              ? `महादशा परिवर्तन संक्रमण काल: ${fromMd} महादशा समाप्त होकर ${toMd} महादशा आरंभ होने जा रही है। अंतिम छिद्र दशा (${currentAntar.planet} AD) के दौरान ऊर्जा परिवर्तन और विशेष सावधानी आवश्यक है।` 
              : `Major Mahadasha Transition from ${fromMd} to ${toMd}. Final Antardasha (${currentAntar.planet} AD) of ${fromMd} Mahadasha acting as Chhidra Dasha bridge.`,
            activeTill: currentAntar?.startDate && currentAntar?.endDate
              ? formatVedicRange(currentAntar.startDate, currentAntar.endDate)
              : '23 Jun 2026 to 19 Oct 2027'
          });
        }
      }
    }

    return alerts;
  }, [kundali, isHi, isDhaiyyaBlinking]);

  return (
    <div className="w-full bg-[#0E2E2D] text-[#FAF6ED] border-b border-[#08201F] sticky top-0 z-30 shadow-md backdrop-blur-md transform-gpu">
      {/* Custom Keyframes Style for Gentle Alert Pulse */}
      <style>{`
        @keyframes gentleAlertPulse {
          0% { opacity: 1; transform: scale(1); }
          15% { opacity: 0.35; transform: scale(1.18); }
          30% { opacity: 1; transform: scale(1); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-gentle-alert {
          animation: gentleAlertPulse 3.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>

      <div className={`w-full px-2 sm:px-3 flex items-center justify-between gap-1 sm:gap-2 select-none transition-all duration-200 ${
        isSticky ? 'py-0.5 text-[10px] min-h-[22px]' : 'py-1.5 sm:py-2 text-xs'
      }`}>
        {/* 1. LEFT: JATAKA NAME */}
        <div className="flex items-center gap-1.5 min-w-0 shrink-0">
          <div
            className={`flex items-center gap-1.5 rounded-xl border border-[#D4AF37]/80 hover:border-[#FCD34D] bg-[#0A2423] shadow-xs transition-all ${
              isSticky ? 'px-2 py-0.5' : 'px-2.5 sm:px-3 py-1'
            }`}
            title="Jataka Name"
          >
            <div className="flex flex-col min-w-0">
              <div className="flex items-center gap-1.5">
                <h2
                  className={`font-bold font-serif tracking-wide truncate text-[#FCD34D] ${
                    isSticky ? 'text-xs sm:text-sm max-w-[100px] sm:max-w-[180px]' : 'text-sm sm:text-base max-w-[140px] sm:max-w-[240px]'
                  }`}
                  title={profileName}
                >
                  {profileName}
                </h2>

                {/* Profile Switcher Dropdown (Names Only, Next to Active Jataka Profile Name) */}
                {userSavedKundalis.length > 0 && (
                  <div className="relative inline-flex items-center shrink-0" ref={quickProfileMenuRef}>
                    <button
                      id="btn-quick-profile-switch"
                      ref={quickProfileBtnRef}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsQuickProfileOpen((prev) => !prev);
                      }}
                      title="Switch active Kundali profile"
                      className="p-1 rounded-md text-[#A6BCB4] hover:text-[#FCD34D] hover:bg-[#133E3C] transition cursor-pointer shrink-0 inline-flex items-center gap-0.5"
                    >
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isQuickProfileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isQuickProfileOpen && (
                      <div
                        id="quick-profile-popover"
                        className="absolute left-0 top-full mt-2 w-56 max-h-80 overflow-y-auto bg-[#0E2E2D] border border-[#1C4D4A] rounded-xl shadow-2xl py-2 z-50 animate-fade-in text-left"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-3 py-1.5 text-[10px] uppercase font-bold tracking-wider text-[#A6BCB4] border-b border-[#1C4D4A] mb-1">
                          Saved Profiles ({userSavedKundalis.length})
                        </div>
                        <div className="divide-y divide-[#1C4D4A]/40">
                          {userSavedKundalis.map((sk) => {
                            const isCurrent = kundali?.id === sk.id || kundali?.birthDetails?.name === sk.birthDetails.name;
                            return (
                              <button
                                key={sk.id || sk.birthDetails.name}
                                type="button"
                                onClick={() => {
                                  if (onLoadSavedKundali) {
                                    onLoadSavedKundali(sk);
                                  }
                                  setIsQuickProfileOpen(false);
                                }}
                                className={`w-full text-left px-3 py-2 flex items-center justify-between text-xs transition-colors cursor-pointer ${
                                  isCurrent
                                    ? 'bg-[#133E3C] text-[#FCD34D] font-bold'
                                    : 'hover:bg-[#133E3C]/50 text-[#FAF6ED]'
                                }`}
                              >
                                <span className="font-semibold text-xs truncate">{sk.birthDetails.name || 'Unnamed Chart'}</span>
                                {isCurrent && <Check className="w-3.5 h-3.5 text-[#FCD34D] shrink-0 ml-2" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {handleEdit && !isSticky && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="p-1 rounded-md text-[#A6BCB4] hover:text-[#FCD34D] hover:bg-[#133E3C] transition cursor-pointer shrink-0"
                    title={isHi ? 'प्रोफ़ाइल विवरण संपादित करें' : 'Edit profile birth details'}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              {!isSticky && (
                <span className="text-[10px] text-[#A6BCB4] font-mono leading-none">
                  {dobStr} • {timeStr}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 2. MIDDLE: STREAMLINED UNIFORM TELEMETRY & KARMIC ALERTS */}
        <div className="flex-1 min-w-0 flex items-center gap-1 sm:gap-1.5 overflow-x-auto no-scrollbar py-0.2">
          {/* 2-Tier Stacked Lagna (Top) and Moon (Bottom) Badge */}
          <div 
            className={`flex flex-col justify-center rounded-xl bg-[#0A2423] border border-[#1C4D4A] shadow-xs font-mono shrink-0 transition-all ${
              isSticky ? 'px-2 py-0 text-[9px] leading-tight' : 'px-2.5 sm:px-3 py-0.5 text-[10px] sm:text-[10.5px] leading-snug'
            }`}
            title={`Lagna (Ascendant): ${resolvedLagnaSign} (${resolvedLagnaNum}) | Moon Sign: ${resolvedMoonSign} (${resolvedMoonNum})${resolvedMoonNak ? ` • Nakshatra: ${resolvedMoonNak}` : ''}`}
          >
            {/* Top Row: Lagna */}
            <div className="flex items-center gap-1.5" title={isHi ? 'लग्न (Lagna / Ascendant)' : 'Lagna (Ascendant)'}>
              <Sunrise className={isSticky ? 'w-2 h-2 text-[#D4AF37] shrink-0' : 'w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#D4AF37] shrink-0'} />
              <span className="font-bold font-mono text-[#FAF6ED]">
                {resolvedLagnaSign} ({resolvedLagnaNum})
              </span>
              {!isSticky && (kundali?.birthDetails?.btrStatus === 'VERIFIED' ? (
                <span 
                  className="px-1 py-0 rounded text-[7.5px] font-bold font-mono uppercase bg-emerald-700 text-white border border-emerald-500 shadow-2xs ml-0.5"
                  title={`Birth Time Rectified (Verified accuracy delta: ${kundali.birthDetails.rectifiedDeltaSeconds ?? 0}s)`}
                >
                  BTR
                </span>
              ) : kundali?.birthDetails?.isRectified ? (
                <span 
                  className="px-1 py-0 rounded text-[7.5px] font-bold font-mono uppercase bg-[#D4AF37] text-[#082120] border border-[#FCD34D] shadow-2xs ml-0.5"
                  title={`Birth Time Rectified (Shift: ${(kundali.birthDetails.rectifiedDeltaSeconds ?? 0) >= 0 ? '+' : ''}${kundali.birthDetails.rectifiedDeltaSeconds ?? 0}s)`}
                >
                  BTR
                </span>
              ) : null)}
            </div>

            {/* Bottom Row: Moon */}
            <div className="flex items-center gap-1.5 border-t border-[#1C4D4A] pt-0.5 mt-0.5" title={isHi ? 'चन्द्र राशि (Moon Sign / Rashi)' : 'Moon Sign (Rashi)'}>
              <Moon className={isSticky ? 'w-2 h-2 text-[#D4AF37] shrink-0' : 'w-2.5 h-2.5 sm:w-3 sm:h-3 text-[#D4AF37] shrink-0'} />
              <span className="font-semibold font-mono text-[#A6BCB4]">
                {resolvedMoonSign} ({resolvedMoonNum}){resolvedMoonNak && !isSticky ? ` • ${resolvedMoonNak}` : ''}
              </span>
            </div>
          </div>

          {/* 2-Tier Stacked Age (Top) and Location (Bottom) Badge */}
          <div className="relative inline-flex flex-col shrink-0" ref={locationContainerRef}>
            <div 
              className={`flex flex-col justify-center rounded-xl bg-[#0A2423] border border-[#1C4D4A] shadow-xs font-mono shrink-0 transition-all ${
                isSticky ? 'px-2 py-0 text-[9px] leading-tight' : 'px-2.5 sm:px-3 py-0.5 text-[10px] sm:text-[10.5px] leading-snug'
              }`}
            >
              {/* Top Row: Age */}
              <div className="flex items-center gap-1">
                <Hourglass className={isSticky ? 'w-2 h-2 text-[#D4AF37] shrink-0' : 'w-2.5 h-2.5 text-[#D4AF37] shrink-0'} />
                <span className="font-semibold text-[#FAF6ED]">{resolvedAge}</span>
              </div>

              {/* Bottom Row: Location Button */}
              <button
                type="button"
                id="profile-location-toggle-button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCoordinates((prev) => !prev);
                }}
                className={`flex items-center gap-1 border-t border-[#1C4D4A] pt-0.5 mt-0.5 cursor-pointer text-left select-none transition-colors group ${
                  showCoordinates ? 'text-[#FCD34D] font-bold' : 'text-[#A6BCB4] hover:text-[#FAF6ED]'
                }`}
                title={isHi ? 'अक्षांश एवं देशांतर देखने के लिए क्लिक करें' : `Click to view coordinates & full place (${resolvedPlace})`}
              >
                <MapPin className={isSticky ? 'w-2 h-2 text-[#D4AF37] shrink-0' : 'w-2.5 h-2.5 text-[#D4AF37] shrink-0'} />
                <span className="font-medium max-w-[80px] sm:max-w-[120px] truncate">
                  {compactPlace}
                </span>
                <ChevronDown className={`w-2.5 h-2.5 text-[#D4AF37] opacity-80 transition-transform duration-150 ${showCoordinates ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {/* Coordinates Popover */}
            {showCoordinates && (
              <div
                id="profile-coordinates-popover"
                role="dialog"
                aria-label="Geographical Coordinates"
                className="absolute left-0 top-full mt-2 z-50 w-72 sm:w-80 bg-[#0E2E2D] border border-[#1C4D4A] rounded-xl shadow-2xl p-3.5 text-xs text-[#FAF6ED] space-y-2.5 animate-fadeIn select-text"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between border-b border-[#1C4D4A] pb-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm">🌐</span>
                    <div>
                      <p className="font-bold text-[#FCD34D] text-xs leading-none">
                        {isHi ? 'भौगोलिक निर्देशांक' : 'Geographical Coordinates'}
                      </p>
                      <p className="text-[10px] text-[#A6BCB4] mt-0.5 truncate max-w-[210px]" title={resolvedPlace}>
                        {resolvedPlace}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowCoordinates(false)}
                    className="text-[#A6BCB4] hover:text-white p-1 rounded hover:bg-[#133E3C] transition cursor-pointer"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-2 bg-[#071F1E] rounded-lg p-2.5 border border-[#143B39]">
                  <div className="space-y-0.5">
                    <span className="text-[10px] font-semibold text-[#A6BCB4] uppercase tracking-wider block">
                      {isHi ? 'अक्षांश' : 'Latitude'}
                    </span>
                    <p className="font-mono font-bold text-[#FCD34D] text-xs">
                      {formatDec(finalLat, true, 4)}
                    </p>
                  </div>
                  <div className="space-y-0.5 border-l border-[#143B39] pl-2">
                    <span className="text-[10px] font-semibold text-[#A6BCB4] uppercase tracking-wider block">
                      {isHi ? 'देशांतर' : 'Longitude'}
                    </span>
                    <p className="font-mono font-bold text-[#FCD34D] text-xs">
                      {formatDec(finalLon, false, 4)}
                    </p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-[11px] text-[#A6BCB4] px-1">
                  <span>{isHi ? 'समय क्षेत्र:' : 'Timezone:'}</span>
                  <span className="font-mono font-semibold text-[#FAF6ED]">{finalTzName}</span>
                </div>
              </div>
            )}
          </div>

          {/* STREAMLINED KARMIC ALERT TICKER BADGES */}
          {activeKarmicAlerts.length > 0 && (
            <div className="flex items-center gap-1.5 shrink-0 pl-1 border-l border-[#1C4D4A]">
              {activeKarmicAlerts.map((alert) => (
                <button
                  key={alert.key}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedKarmicAlert(alert);
                  }}
                  title={alert.desc + ' • Click to view detailed Upaya & Vedic remediation'}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-xl ${alert.bg} ${alert.text} border ${alert.border} font-mono text-[10.5px] font-bold shadow-2xs shrink-0 cursor-pointer hover:scale-105 transition-all`}
                >
                  <span className="shrink-0">{alert.icon}</span>
                  <span>{alert.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 3. RIGHT: RIGHT-ALIGNED JATAKA PROFILE & WORKSPACE MODES CONTROL (MATCHED STYLING) */}
        <div className="flex items-center gap-1.5 shrink-0 z-40">
          {/* Right-aligned Jataka Profile Button */}
          <button
            type="button"
            id="jataka-profile-blueprint-button"
            onClick={() => setShowBlueprintModal(true)}
            className={`inline-flex items-center gap-1.5 rounded-xl font-semibold transition-all cursor-pointer shadow-xs shrink-0 select-none border border-[#1C4D4A] hover:border-[#D4AF37]/60 ${
              isSticky
                ? 'px-2 py-0 text-[9.5px] bg-[#0A2423] hover:bg-[#133E3C] text-[#FAF6ED]'
                : 'px-2.5 sm:px-3 h-7 text-xs bg-[#0A2423] hover:bg-[#133E3C] text-[#FAF6ED]'
            }`}
            title={isHi ? 'सम्पूर्ण जातक विवरण (खगोलीय काल माप, अवकहड़ा व ग्रह स्पष्ट)' : 'View Jataka Profile (Solar Time Math, Avakahada & Planetary Ephemeris)'}
          >
            <Compass className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" />
            <span className="font-semibold text-[#FAF6ED]">{isHi ? 'जातक विवरण' : 'Jataka Profile'}</span>
          </button>

          {/* Workspace Modes - Segmented Control (Matched styling with Jataka Profile) */}
          {!isSticky && !hideWorkspaceSegmentedControl && (
            <div className="flex items-center gap-0.5 p-0.5 bg-[#071F1E] border border-white/15 rounded-xl shrink-0 h-7">
              <button
                type="button"
                onClick={() => onSelectWorkspaceMode?.('tools')}
                className={`px-2.5 sm:px-3 h-6 rounded-lg text-xs transition-all flex items-center gap-1.5 cursor-pointer select-none font-semibold ${
                  activeWorkspaceMode === 'tools'
                    ? 'bg-[#D4AF37] text-[#082120] border border-[#D4AF37] shadow-xs font-bold'
                    : 'text-[#E0EAE6] hover:text-white hover:bg-white/5'
                }`}
                title={isHi ? 'ज्योतिषी उपकरण' : 'Tools'}
              >
                <Compass className={`w-3.5 h-3.5 ${activeWorkspaceMode === 'tools' ? 'text-[#082120]' : 'text-[#A6BCB4]'}`} />
                <span className="hidden sm:inline">{isHi ? 'टूल' : 'Tools'}</span>
              </button>

              <button
                type="button"
                onClick={() => onSelectWorkspaceMode?.('astrologer')}
                className={`px-2.5 sm:px-3 h-6 rounded-lg text-xs transition-all flex items-center gap-1.5 cursor-pointer select-none font-semibold ${
                  activeWorkspaceMode === 'astrologer'
                    ? 'bg-[#D4AF37] text-[#082120] border border-[#D4AF37] shadow-xs font-bold'
                    : 'text-[#E0EAE6] hover:text-white hover:bg-white/5'
                }`}
                title={isHi ? 'कार्यपीठ' : 'Workbench'}
              >
                <Target className={`w-3.5 h-3.5 ${activeWorkspaceMode === 'astrologer' ? 'text-[#082120]' : 'text-[#A6BCB4]'}`} />
                <span className="hidden sm:inline">{isHi ? 'कार्यपीठ' : 'Workbench'}</span>
              </button>

              <button
                type="button"
                id="header-d10-studio-button"
                onClick={() => onSelectWorkspaceMode?.('enterprise-studio')}
                className={`px-2.5 sm:px-3 h-6 rounded-lg text-xs transition-all flex items-center gap-1.5 cursor-pointer select-none font-semibold ${
                  activeWorkspaceMode === 'enterprise-studio'
                    ? 'bg-[#D4AF37] text-[#082120] border border-[#D4AF37] shadow-xs font-bold'
                    : 'text-[#E0EAE6] hover:text-white hover:bg-white/5'
                }`}
                title={isHi ? 'वैदिक डेस्टिनी स्टूडियो' : 'Vedic Destiny Studio'}
              >
                <Compass className={`w-3.5 h-3.5 ${activeWorkspaceMode === 'enterprise-studio' ? 'text-[#082120]' : 'text-[#A6BCB4]'}`} />
                <span className="hidden sm:inline">{isHi ? 'वैदिक डेस्टिनी' : 'Vedic Destiny'}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Jataka Birth Blueprint Master View (Layout A: Top Trinity Charts + Bottom 3 Telemetry Columns) */}
      {showBlueprintModal && kundali && (
        <JatakaBirthBlueprintModal
          isOpen={showBlueprintModal}
          onClose={() => setShowBlueprintModal(false)}
          kundali={kundali}
          lang={lang as any}
        />
      )}

      {/* Karmic Transit & Upaya Detail Modal (Global Light Theme - Cream, Gold & Teal) */}
      {selectedKarmicAlert && typeof document !== 'undefined' && createPortal(
        <div
          id="karmic-alert-modal-overlay"
          className="fixed inset-0 z-[9999] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto animate-fade-in"
          onClick={() => setSelectedKarmicAlert(null)}
          onKeyDown={(e) => e.key === 'Escape' && setSelectedKarmicAlert(null)}
          tabIndex={0}
        >
          <div
            id="karmic-alert-modal-content"
            className="w-full max-w-xl my-auto max-h-[85vh] overflow-y-auto bg-[#FFFDF7] border-2 border-[#D4AF37]/70 rounded-3xl shadow-2xl p-6 text-[#263332] space-y-5 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-[#E5DDD0] pb-3">
              <div className="flex items-center gap-3">
                <span className="text-3xl p-2.5 rounded-2xl bg-[#FAF6ED] border border-[#E5DDD0] shadow-2xs">{selectedKarmicAlert.icon}</span>
                <div>
                  <h3 className="text-lg font-serif font-bold text-[#174C4B]">
                    {selectedKarmicAlert.label}
                  </h3>
                  <p className="text-xs text-[#71807C] font-mono mt-0.5">
                    {isHi ? 'वैदिक खगोलीय काल एवं उपाय निर्देशिका' : 'Vedic Transit & Karmic Remedial Dossier'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedKarmicAlert(null)}
                className="text-[#71807C] hover:text-[#174C4B] p-1.5 rounded-lg hover:bg-[#F7F3EA] transition cursor-pointer"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4 text-xs">
              {/* Active Period Banner */}
              <div className="bg-[#F7F3EA] rounded-2xl p-3 border border-[#E5DDD0] flex items-center justify-between shadow-2xs">
                <div className="flex items-center gap-2">
                  <span className="text-sm">⏳</span>
                  <span className="font-bold text-[#174C4B] uppercase text-[10px] tracking-wider">
                    {isHi ? 'सक्रिय अवधि (Active Period):' : 'Active Period:'}
                  </span>
                </div>
                <span className="font-mono font-semibold text-xs text-[#263332] bg-white px-2.5 py-1 rounded-lg border border-[#E5DDD0]">
                  {selectedKarmicAlert.activeTill || '03 Jun 2027 to 19 Oct 2027'}
                </span>
              </div>

              {/* Astrological Context */}
              <div className="bg-[#FAF6ED] rounded-2xl p-4 border border-[#E5DDD0] space-y-1.5 shadow-2xs">
                <span className="text-[10px] uppercase font-bold tracking-wider text-[#B58A4A] block">
                  {isHi ? 'खगोलीय प्रभाव विवरण' : 'Astrological Context & Significance'}
                </span>
                <p className="text-[#263332] leading-relaxed font-sans text-sm">
                  {selectedKarmicAlert.desc}
                </p>
              </div>

              {/* Practical Lifestyle & Vedic Remedies (Separated & Planet-Specific) */}
              {(() => {
                const activePlanet = selectedKarmicAlert?.key?.includes('markesh-md') || selectedKarmicAlert?.key?.includes('markesh-dual')
                  ? kundali?.dashas?.find((d: any) => d.isCurrent)?.planet
                  : selectedKarmicAlert?.key?.includes('markesh-ad')
                  ? kundali?.dashas?.find((d: any) => d.isCurrent)?.antardashas?.find((a: any) => a.isCurrent)?.planet
                  : selectedKarmicAlert?.key?.includes('sade-sati') || selectedKarmicAlert?.key?.includes('dhaiyya')
                  ? 'Saturn'
                  : 'General';

                const p = (activePlanet || '').toLowerCase();
                const key = selectedKarmicAlert.key;

                const advice = (() => {
                  if (key.includes('sade-sati') || key.includes('dhaiyya') || p.includes('saturn')) {
                    return {
                      practical: isHi 
                        ? 'धैर्य और अनुशासन बनाए रखें। कार्यस्थल पर जल्दबाजी न करें और नियमित रूप से टहलने की आदत डालें।' 
                        : 'Maintain patience and steady discipline. Avoid rushing your work and take daily grounding walks in nature.',
                      mantra: isHi ? 'ॐ शं शनैश्चराय नमः (शनिवार को 108 बार)' : 'Chant "Om Sham Shanicharaya Namah" (108 times on Saturdays)',
                      worship: isHi ? 'शनिवार की शाम पीपल के पेड़ के पास सरसों के तेल का दीया जलाएं।' : 'Light a mustard oil lamp near a Peepal tree on Saturday evenings',
                      charity: isHi ? 'शनिवार को काले तिल, सरसों का तेल या जरूरतमंद श्रमिकों को कंबल दान करें।' : 'Donate black sesame seeds, mustard oil, or blankets to workers on Saturdays'
                    };
                  }
                  if (p.includes('mars') || p.includes('mangal') || key.includes('markesh')) {
                    return {
                      practical: isHi 
                        ? 'अपनी ऊर्जा को जिम वर्कआउट, खेलकूद या ब्रिस्क रनिंग में लगाएं। अनावश्यक बहस या गुस्से से दूर रहें।' 
                        : 'Channel high physical energy into workouts, sports, or brisk walking. Avoid impulsive arguments and rash driving.',
                      mantra: isHi ? 'नित्य हनुमान चालीसा का पाठ करें (या ॐ भौमदवाय नमः)' : 'Recite Hanuman Chalisa daily for inner strength and courage',
                      worship: isHi ? 'मंगलवार को हनुमान जी के मंदिर में गुड़ और चने का प्रसाद चढ़ाएं।' : 'Offer prayers and jaggery at a Hanuman temple on Tuesdays',
                      charity: isHi ? 'मंगलवार को लाल मसूर की दाल या अन्न का दान करें।' : 'Donate red lentils (masoor dal) or perform food charity on Tuesdays'
                    };
                  }
                  if (p.includes('jupiter') || p.includes('guru')) {
                    return {
                      practical: isHi 
                        ? 'बड़ों व गुरुओं की सलाह का सम्मान करें और पैसों के लेन-देन में सूझबूझ रखें।' 
                        : 'Seek guidance from elders and mentors, and maintain thoughtful budgeting in financial decisions.',
                      mantra: isHi ? 'ॐ बृं बृहस्पतये नमः (गुरुवार को 108 बार)' : 'Chant "Om Brim Brihaspataye Namah" on Thursdays',
                      worship: isHi ? 'गुरुवार को केले या पीपल के पेड़ में जल अर्पित करें।' : 'Water the Peepal or Banana tree with respect on Thursdays',
                      charity: isHi ? 'गुरुवार को चने की दाल, हल्दी या पीली मिठाई का दान करें।' : 'Donate yellow chana dal, turmeric, or yellow sweets on Thursdays'
                    };
                  }
                  if (p.includes('venus') || p.includes('shukra')) {
                    return {
                      practical: isHi 
                        ? 'रिश्तों में प्यार और तालमेल बनाए रखें। अनावश्यक खर्चों पर नियंत्रण रखें।' 
                        : 'Nurture warmth and harmony in relationships and practice mindful budgeting.',
                      mantra: isHi ? 'श्री सूक्त का पाठ करें या ॐ शुं शुक्राय नमः जपें।' : 'Recite Shri Sukta or chant "Om Shukraya Namah"',
                      worship: isHi ? 'माता लक्ष्मी की आराधना करें और घर में साफ-सफाई रखें।' : 'Worship Goddess Lakshmi and maintain a clean, peaceful home environment',
                      charity: isHi ? 'शुक्रवार को सफेद मिठाई या चावल का दान करें।' : 'Donate white sweets or rice on Fridays'
                    };
                  }
                  if (key.includes('chhidra')) {
                    return {
                      practical: isHi 
                        ? 'बदलाव के इस दौर में खुद पर मानसिक दबाव न डालें। पर्याप्त नींद लें और योग निद्रा का अभ्यास करें।' 
                        : 'Avoid rushing into major decisions during this transitional bridge. Prioritize gentle rest and Yoga Nidra.',
                      mantra: isHi ? 'विष्णु सहस्त्रनाम का पाठ सुनें या महामृत्युंजय मंत्र जपें।' : 'Listen to or chant Vishnu Sahasranama and Maha Mrityunjaya Mantra',
                      worship: isHi ? 'अपने कुलदेवता और पूर्वजों का स्मरण कर आभार व्यक्त करें।' : 'Offer gratitude and prayers to your ancestors (Pitru) and Kuldevta',
                      charity: isHi ? 'पक्षियों को दाना दें और जरूरतमंदों को भोजन (अन्न दान) कराएं।' : 'Feed birds and perform food charity (Annam Daan) for smooth transition'
                    };
                  }
                  return {
                    practical: isHi 
                      ? 'संतुलित दिनचर्या अपनाएं, सकारात्मक सोच रखें और अपने स्वास्थ्य का पूरा ध्यान रखें।' 
                      : 'Maintain a balanced daily routine, healthy nutrition, and emotional well-being.',
                    mantra: isHi ? 'महामृत्युंजय मंत्र अथवा गायत्री मंत्र का नित्य जाप करें।' : 'Chant Mahamrityunjaya Mantra or Gayatri Mantra daily for peace and vitality',
                    worship: isHi ? 'नियमित रूप से अपने इष्टदेव की पूजा करें।' : 'Offer regular prayers to your Ishta Devta with a pure heart',
                    charity: isHi ? 'गौमाता को हरा चारा खिलाएं और अन्न दान करें।' : 'Feed cows with green fodder and perform food charity'
                  };
                })();

                return (
                  <div className="space-y-3">
                    {/* Practical Lifestyle & Habits Card */}
                    <div className="bg-[#FAF6ED] rounded-2xl p-4 border border-[#E5DDD0] space-y-2 shadow-2xs">
                      <div className="flex items-center gap-2 text-[#174C4B] font-bold text-xs uppercase tracking-wider">
                        <span>💡</span>
                        <span>{isHi ? 'व्यावहारिक आदतें एवं जीवनशैली' : 'Practical Lifestyle & Habits'}</span>
                      </div>
                      <p className="text-[#263332] text-sm leading-relaxed font-sans">
                        {advice.practical}
                      </p>
                    </div>

                    {/* Vedic Mantras, Worship & Charity Card */}
                    <div className="bg-[#F7F3EA] rounded-2xl p-4 border border-[#E5DDD0] space-y-3 shadow-2xs">
                      <div className="flex items-center gap-2 text-[#174C4B] font-bold text-xs uppercase tracking-wider">
                        <span>🪔</span>
                        <span>{isHi ? 'वैदिक मंत्र, पूजा एवं दान' : 'Vedic Mantras, Worship & Charity'}</span>
                      </div>
                      <div className="space-y-2 text-xs text-[#263332]">
                        <div className="flex items-start gap-2.5 bg-[#FFFDF7] p-2.5 rounded-xl border border-[#E5DDD0]">
                          <span className="text-[#D4AF37] font-bold mt-0.5">•</span>
                          <div>
                            <strong className="text-[#174C4B]">{isHi ? 'मंत्र जप (Mantra):' : 'Mantra Chanting:'}</strong>{' '}
                            {advice.mantra}
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5 bg-[#FFFDF7] p-2.5 rounded-xl border border-[#E5DDD0]">
                          <span className="text-[#D4AF37] font-bold mt-0.5">•</span>
                          <div>
                            <strong className="text-[#174C4B]">{isHi ? 'पूजा एवं आराधना (Worship):' : 'Worship & Prayers:'}</strong>{' '}
                            {advice.worship}
                          </div>
                        </div>
                        <div className="flex items-start gap-2.5 bg-[#FFFDF7] p-2.5 rounded-xl border border-[#E5DDD0]">
                          <span className="text-[#D4AF37] font-bold mt-0.5">•</span>
                          <div>
                            <strong className="text-[#174C4B]">{isHi ? 'दान एवं सेवा (Charity):' : 'Charity & Service:'}</strong>{' '}
                            {advice.charity}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-end pt-2 border-t border-[#E5DDD0]">
              <button
                type="button"
                onClick={() => setSelectedKarmicAlert(null)}
                className="px-5 py-2.5 rounded-xl bg-[#174C4B] hover:bg-[#133E3C] text-white font-bold text-xs transition cursor-pointer shadow-md"
              >
                {isHi ? 'समझ गया (Close)' : 'Understood'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default JatakaProfileHeader;
